import { useCurrentProject } from "./useCurrentProject";
import type { FeatureKey } from "@/api/types";

/**
 * Per-project feature flag check. Returns true when the feature is
 * enabled (the default for any key not present in
 * `project.features_enabled`) or when there's no current project
 * yet — surfaces falling back to "available" while the project
 * fetch is in flight prevents a flash of empty UI.
 *
 * Usage:
 *   const showOutreach = useFeature("lemlist");
 *   if (!showOutreach) return null;
 */
export function useFeature(key: FeatureKey): boolean {
  const { project } = useCurrentProject();
  // No project loaded yet → assume on. Once the project comes back
  // the consumer re-renders with the real flag.
  if (!project) return true;
  const map = project.features_enabled;
  if (!map) return true;
  // Missing key in the map → default to enabled, matches the
  // backend's "absent = on" semantics for forward compat with new
  // features that older projects haven't been migrated for.
  return map[key] !== false;
}
